import {
  AccessTime,
  Approval as ApprovalIcon,
  Cancel,
  Check,
  DirectionsRun,
  Done,
  DoNotDisturb,
  ErrorOutline,
  QuestionMark,
  SportsScore,
  Start,
} from "@mui/icons-material";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { ReactNode } from "react";
import {
  Approval,
  AutoApprovedReason,
  CancelReason,
  Task,
  TaskStatus,
} from "../../../app/backendTypes";
import { tsToDate } from "../../../app/utils";
import ErrorSpan from "../ErrorSpan";
import LoadingSpan from "../LoadingSpan";
import UserSpan from "../users/UserSpan";
import UserSpanLoader from "../users/UserSpanLoader";
import ApprovalNotificationList from "./ApprovalNotificationList";
import ApproverLoader from "./ApproverLoader";
import ParentSpan from "./ParentSpan";

function TaskHistory(props: { task: Task; open: boolean }) {
  const { task, open } = props;

  const created = tsToDate(task.created);

  const steps: [
    {
      icon: ReactNode;
      primary: ReactNode;
      secondary: ReactNode;
      content: ReactNode;
    }
  ] = [
    {
      icon: <Start color="disabled" />,
      primary: <Typography color="text.secondary">Created</Typography>,
      secondary: (
        <Typography color="text.secondary" variant="body2">
          {`${created.toDateString()} at ${created.toLocaleTimeString()} by `}
          <ParentSpan parent={task.parent} variant="body2" />
        </Typography>
      ),
      content: <></>,
    },
  ];

  const cancelled = task.status.kind === "Cancelled";
  let denied = false;
  let allApproved = true;

  task.approvals?.forEach((approval, index) => {
    const step = createApprovalStep(
      approval,
      index,
      task.id,
      denied,
      cancelled,
      !open
    );

    if (approval.status.kind === "Denied") denied = true;
    if (approval.status.kind !== "Approved") allApproved = false;

    steps.push(step);
  });

  steps.push(createFinalStep(allApproved, task.status, denied, task.updated));

  return (
    <List>
      {steps.map((step, index) => (
        <Box key={index}>
          <ListItem alignItems="flex-start">
            <ListItemIcon>{step.icon}</ListItemIcon>
            <Box sx={{ width: "100%" }}>
              <ListItemText primary={step.primary} secondary={step.secondary} />
              {step.content}
            </Box>
          </ListItem>
        </Box>
      ))}
    </List>
  );
}

export default TaskHistory;

function createApprovalStep(
  approval: Approval,
  approvalIndex: number,
  taskId: string,
  previousDenied: boolean,
  cancelled: boolean,
  skipLoading: boolean
) {
  let icon, primary, secondary, content;

  switch (approval.status.kind) {
    case "Idle": {
      icon = <AccessTime color="disabled" />;
      primary = (
        <Typography color="text.secondary">{`${approval.kind.kind} approval`}</Typography>
      );
      secondary =
        previousDenied || cancelled ? (
          <></>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Needs to be approved by{" "}
            <ApproverLoader
              taskId={taskId}
              approvalIndex={approvalIndex}
              render={(user) => <UserSpan user={user} variant="body2" />}
              renderError={(error) => (
                <ErrorSpan error={error} variant="body2" />
              )}
              renderLoading={() => <LoadingSpan variant="body2" />}
              skipLoading={skipLoading}
            />
          </Typography>
        );
      break;
    }

    case "Notified": {
      icon = <ApprovalIcon color={cancelled ? "disabled" : "secondary"} />;
      primary = (
        <Typography
          color={cancelled ? "text.secondary" : "inherit"}
        >{`${approval.kind.kind} approval`}</Typography>
      );
      secondary = cancelled ? (
        <></>
      ) : (
        <Typography variant="body2">
          Needs to be approved by{" "}
          <ApproverLoader
            taskId={taskId}
            approvalIndex={approvalIndex}
            render={(user) => <UserSpan user={user} variant="body2" />}
            renderError={(error) => <ErrorSpan error={error} variant="body2" />}
            renderLoading={() => <LoadingSpan variant="body2" />}
            skipLoading={skipLoading}
          />
        </Typography>
      );
      content = (
        <ApprovalNotificationList history={approval.status.inner.history} />
      );
      break;
    }

    case "Approved": {
      icon = <Check color="success" />;
      primary = (
        <Typography color="text.secondary">{`${approval.kind.kind} approved`}</Typography>
      );
      const date = tsToDate(approval.status.inner.ts);
      secondary = (
        <Typography variant="body2" color="text.secondary">
          {`${date.toDateString()} at ${date.toLocaleTimeString()} by `}
          <UserSpanLoader id={approval.status.inner.user_id} variant="body2" />
          {approval.status.inner.note && (
            <>
              <br />
              <br />
              <Typography variant="caption">
                Attached note:{" "}
                <Typography
                  component="span"
                  fontStyle="italic"
                  variant="caption"
                >
                  {approval.status.inner.note}
                </Typography>
              </Typography>
            </>
          )}
          {approval.status.inner.original_approver && (
            <>
              <br />
              <br />
              <Typography variant="caption">
                Original approver was{" "}
                <Typography component="span" variant="caption">
                  <UserSpanLoader
                    id={approval.status.inner.original_approver}
                    variant="caption"
                  />
                </Typography>
              </Typography>
            </>
          )}
        </Typography>
      );
      break;
    }

    case "AutoApproved": {
      icon = <Check color="success" />;
      primary = (
        <Typography color="text.secondary">{`${approval.kind.kind} approved automatically`}</Typography>
      );

      let autoApprovedReason;
      switch (approval.status.inner.reason) {
        case AutoApprovedReason.ApproverIsParent: {
          autoApprovedReason = "beacause approver created the task";
          break;
        }

        case AutoApprovedReason.ApproverPreference: {
          autoApprovedReason = "on account of approver preference";
          break;
        }

        default: {
          autoApprovedReason = "for unknown reasons";
        }
      }

      const date = tsToDate(approval.status.inner.ts);
      secondary = (
        <Typography variant="body2" color="text.secondary">
          {`${date.toDateString()} at ${date.toLocaleTimeString()} by `}
          <UserSpanLoader id={approval.status.inner.user_id} variant="body2" />
          <br />
          {`Automatically approved ${autoApprovedReason}`}
        </Typography>
      );
      break;
    }

    case "Denied": {
      icon = <DoNotDisturb color="error" />;
      primary = (
        <Typography color="text.secondary">{`${approval.kind.kind} denied`}</Typography>
      );
      const date = tsToDate(approval.status.inner.ts);
      secondary = (
        <Typography variant="body2" color="text.secondary">
          {`${date.toDateString()} at ${date.toLocaleTimeString()} by `}
          <UserSpanLoader id={approval.status.inner.user_id} variant="body2" />
          {approval.status.inner.reason && (
            <>
              <br />
              <br />
              <Typography component="span" variant="caption">
                Reason:{" "}
                <Typography
                  component="span"
                  variant="caption"
                  fontStyle="italic"
                >
                  {approval.status.inner.reason}
                </Typography>
              </Typography>
            </>
          )}
          {approval.status.inner.original_approver && (
            <>
              <br />
              <br />
              <Typography component="span" variant="caption">
                Original approver was{" "}
                <UserSpanLoader
                  id={approval.status.inner.original_approver}
                  variant="caption"
                />
              </Typography>
            </>
          )}
        </Typography>
      );
      break;
    }

    default: {
      icon = <QuestionMark color="warning" />;
      primary = (
        <Typography color="warning">
          We have no idea what is going on
        </Typography>
      );
    }
  }

  return {
    icon,
    primary,
    secondary,
    content,
  };
}

function createFinalStep(
  approved: boolean,
  status: TaskStatus,
  denied: boolean,
  updated: string | undefined
) {
  let icon = <></>;
  let primary = <></>;
  let secondary = <></>;
  const content = <></>;

  if (updated !== undefined) {
    const date = tsToDate(updated);
    secondary = (
      <Typography variant="body2">
        {`${date.toDateString()} at ${date.toLocaleTimeString()}`}
      </Typography>
    );
  }

  switch (status.kind) {
    case "Done": {
      icon = <Done color="success" />;
      primary = <Typography>Done</Typography>;
      break;
    }

    case "Cancelled": {
      icon = <Cancel color="error" />;
      primary = <Typography>{denied ? "Denied" : "Cancelled"}</Typography>;
      secondary = createCancelReasonTypo(status.inner.reason);
      break;
    }

    case "Error": {
      if (approved) {
        icon = <ErrorOutline color="error" />;
        primary = <Typography>Error</Typography>;
      }
      break;
    }

    case "Running": {
      if (approved) {
        icon = <DirectionsRun color="secondary" />;
        primary = <Typography>Running</Typography>;
        secondary = <></>;
      }
      break;
    }

    default: {
      icon = <SportsScore color="disabled" />;
      primary = <Typography color="text.secondary">Run</Typography>;
      secondary = <></>;
    }
  }

  return {
    icon,
    primary,
    secondary,
    content,
  };
}

function createCancelReasonTypo(reason: CancelReason) {
  let comp = <></>;

  switch (reason.kind) {
    case "ByUser": {
      const date = tsToDate(reason.inner.ts);
      comp = (
        <Typography variant="body2">
          {`${date.toDateString()} at ${date.toLocaleTimeString()} by `}
          <UserSpanLoader id={reason.inner.user_id} variant="body2" />
          {reason.inner.reason && (
            <>
              <br />
              <br />
              <Typography component="span" variant="caption">
                Reason:{" "}
                <Typography
                  component="span"
                  variant="caption"
                  fontStyle="italic"
                >
                  {reason.inner.reason}
                </Typography>
              </Typography>
            </>
          )}
        </Typography>
      );
      break;
    }
  }

  return comp;
}
